import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, Button, Switch, Select, Modal, message } from "antd";
import { EditOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import SearchBar from "../../components/searchBar";
import StatusTabs from "../../components/statusTabs";
import AddButton from "../../components/addButton";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import AddMerchantModal from "../../components/modals/AddMerchantModal";
import {
  useGetMerchantsQuery,
  useToggleMerchantStatusMutation,
  useDeleteMerchantMutation,
  useGetMerchantCategoriesQuery,
} from "../../services/merchant";
import usePermissions from "../../hooks/usePermissions";
import { VendorTransactionsRoute } from "../../routes/routepath";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface MerchantRecord {
  key:             string;
  name?:           string;
  mobile?:         string;
  category?:       string;
  isActive?:       boolean;
  totalBillAmount?: number;
  totalPaid?:       number;
  totalDue?:        number;
}

const AMOUNT_COLOR = '#52c41a';
const DUE_COLOR    = '#ff4d4f';

const renderAmount = (v?: number, color?: string) =>
  v === undefined || v === null ? '-' : <span style={{ color }}>₹{v.toLocaleString('en-IN')}</span>;

/* ─── Component ──────────────────────────────────────────────────────── */

const AllMerchant = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [page,        setPage]        = useState(1);
  const [limit,       setLimit]       = useState(10);
  const [searchText,  setSearchText]  = useState("");
  const [activeTab,   setActiveTab]   = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editMerchant, setEditMerchant] = useState<MerchantRecord | null>(null);
  const [toggleMerchantStatus, { isLoading: toggling }] = useToggleMerchantStatusMutation();
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deleteMerchant] = useDeleteMerchantMutation();

  const { data: categoriesResp } = useGetMerchantCategoriesQuery();
  const categoryList = Array.isArray(categoriesResp)
    ? categoriesResp
    : (Array.isArray((categoriesResp as any)?.data) ? (categoriesResp as any).data : []);
  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...categoryList.map((c: any) => ({ value: c._id, label: c.name })),
  ];

  const { data: merchantsResp, isFetching: merchantsLoading } = useGetMerchantsQuery({
    search:     searchText || undefined,
    status:     activeTab as 'all' | 'active' | 'inactive',
    categoryId: categoryFilter !== 'all' ? categoryFilter : undefined,
    page,
    limit,
  });

  const apiData = merchantsResp as any;
  const counts  = apiData?.counts || {};

  const merchants = useMemo<MerchantRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((m: any) => ({
      key:             m._id,
      name:            m.name,
      mobile:          m.mobile,
      category:        m.category,
      isActive:        m.isActive,
      totalBillAmount: m.totalBillAmount,
      totalPaid:       m.totalPaid,
      totalDue:        m.totalDue,
    }));
  }, [apiData]);

  const canToggleStatus = hasPermission('MERCHANT_STATUS_TOGGLE');
  const canUpdate = hasPermission('MERCHANT_UPDATE');
  const canDelete = hasPermission('MERCHANT_DELETE');

  const handleToggleStatus = async (record: MerchantRecord, checked: boolean) => {
    if (!canToggleStatus) return;
    setTogglingId(record.key);
    try {
      await toggleMerchantStatus({ id: record.key, isActive: checked }).unwrap();
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = (record: MerchantRecord) => {
    Modal.confirm({
      title:      'Delete Merchant',
      content:    `Are you sure you want to delete "${record.name}"?`,
      okText:     'Delete',
      okType:     'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteMerchant(record.key).unwrap();
          message.success('Merchant deleted successfully');
        } catch {
          /* error toast handled globally by badRequestHandler */
        }
      },
    });
  };

  const tabsData = [
    { key: 'all',      label: 'All',      count: counts.all      || 0 },
    { key: 'active',   label: 'Active',   count: counts.active   || 0 },
    { key: 'inactive', label: 'Inactive', count: counts.inactive || 0 },
  ];

  const columns = [
    { title: 'S.No',   key: 'sno', width: 60, render: (_: unknown, __: unknown, i: number) => (page - 1) * limit + i + 1 },
    { title: 'Name',     dataIndex: 'name',     key: 'name',
      render: (v?: string, record?: MerchantRecord) => v ? (
        <span
          style={{ color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate(`${VendorTransactionsRoute}?merchantId=${record?.key}`, { state: { entityName: v } })}
        >
          {v}
        </span>
      ) : '-' },
    { title: 'Mobile',   dataIndex: 'mobile',   key: 'mobile',   render: (v?: string) => v || '-' },
    { title: 'Category', dataIndex: 'category', key: 'category', render: (v?: string) => v || '-' },
    { title: 'Total Bill', dataIndex: 'totalBillAmount', key: 'totalBillAmount', render: (v?: number) => renderAmount(v) },
    { title: 'Total Paid', dataIndex: 'totalPaid', key: 'totalPaid', render: (v?: number) => renderAmount(v, AMOUNT_COLOR) },
    { title: 'Total Due',  dataIndex: 'totalDue',  key: 'totalDue',  render: (v?: number) => renderAmount(v, v ? DUE_COLOR : undefined) },
    { title: 'Status', dataIndex: 'isActive', key: 'isActive', align: 'center' as const,
      render: (isActive: boolean, record: MerchantRecord) => (
        <Switch
          checked={isActive}
          disabled={!canToggleStatus}
          loading={toggling && togglingId === record.key}
          onChange={(checked) => handleToggleStatus(record, checked)}
        />
      ) },
    ...(canUpdate || canDelete ? [{
      title: 'Actions', key: 'actions', align: 'center' as const,
      render: (_: unknown, record: MerchantRecord) => {
        const menuItems = [
          ...(canUpdate ? [{ key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: () => setEditMerchant(record) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(record) }] : []),
        ];
        return (
          <Dropdown trigger={['click']} placement="bottomRight" menu={{ items: menuItems }}>
            <Button type="text" icon={<MoreOutlined style={{ fontSize: 20 }} />} />
          </Dropdown>
        );
      },
    }] : []),
  ];

  return (
    <div className="all-merchant-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={(v: string) => { setSearchText(v); setPage(1); }}
            placeholder="Search merchants..."
          />
          <StatusTabs
            activeTab={activeTab}
            onTabChange={(tab: string) => { setActiveTab(tab); setPage(1); }}
            tabs={tabsData}
          />
          <Select
            value={categoryFilter}
            onChange={(v: string) => { setCategoryFilter(v); setPage(1); }}
            style={{ width: 180, height: 41 }}
            showSearch
            optionFilterProp="label"
            options={categoryOptions}
          />
        </div>
        <div className="right-col">
          {hasPermission('MERCHANT_CREATE') && (
            <AddButton onClick={() => setAddModalOpen(true)}>Add Merchant</AddButton>
          )}
        </div>
      </div>

      <div className="merchants-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={merchants}
          loading={merchantsLoading}
          pagination={false}
          rowKey={(record: MerchantRecord) => record.key}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />

      <AddMerchantModal
        visible={addModalOpen || !!editMerchant}
        merchant={editMerchant ? { _id: editMerchant.key, name: editMerchant.name, mobile: editMerchant.mobile, category: editMerchant.category } : null}
        onCancel={() => { setAddModalOpen(false); setEditMerchant(null); }}
      />
    </div>
  );
};

export default AllMerchant;
